import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ThreeSixty as ThreeSixtyIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  CleaningServices as CleaningIcon,
  Style as StyleIcon,
} from "@mui/icons-material";
import { bathAccessoryCategories } from "./bathAccessoriesData";
import { BathAccessoryCategory } from "./bathAccessoriesTypes";
import { getCategoryGradient, handleCall, handleWhatsApp, handleShare } from "../utils";
import TileViewer360 from "../Tiles/TileViewer360";

const BathAccessoriesPage: React.FC = () => {
  const [selectedAccessory, setSelectedAccessory] = useState<BathAccessoryCategory | null>(null);
  const [view360, setView360] = useState<BathAccessoryCategory | null>(null);

  const handle360View = (accessory: BathAccessoryCategory) => {
    if (accessory.images360) {
      setView360(accessory);
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: "#f8f9fa", minHeight: "80vh" }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: "bold",
              color: "#1a237e",
              mb: 2,
              background: "linear-gradient(45deg, #673ab7 30%, #9c27b0 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Bath Accessories Collection
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "#666",
              mb: 2,
              fontWeight: "300",
            }}
          >
            Essential Accessories for Complete Bathroom Experience
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#777",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Complete your bathroom with our extensive range of accessories including towel bars, soap dispensers, 
            mirrors, toilet paper holders, robe hooks, and storage solutions. Each accessory combines functionality 
            with style to enhance your daily bathroom experience.
          </Typography>

          {/* Feature Highlights */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mt: 4, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InventoryIcon sx={{ color: "#673ab7" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                Complete Storage Solutions
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CleaningIcon sx={{ color: "#673ab7" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                Hygienic Design
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StyleIcon sx={{ color: "#673ab7" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>
                Stylish Finishes
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Categories Grid */}
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: "bold",
            color: "#1a237e",
            mb: 4,
            textAlign: "center",
          }}
        >
          Bath Accessory Categories
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 4,
          }}
        >
          {bathAccessoryCategories.map((accessory) => (
            <Box key={accessory.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 24px rgba(103, 58, 183, 0.15)",
                  },
                  borderRadius: "16px",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={() => setSelectedAccessory(accessory)}
              >
                <CardMedia
                  component="div"
                  sx={{
                    height: 200,
                    background: getCategoryGradient("Bath Accessories"),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 2,
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" mb={1}>
                      {accessory.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {accessory.primaryUse[0]}
                    </Typography>
                  </Box>
                </CardMedia>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    {accessory.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    sx={{ lineHeight: 1.5 }}
                  >
                    {accessory.description}
                  </Typography>

                  {/* Key Characteristics */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                    {accessory.keyCharacteristics.slice(0, 3).map((char, charIndex) => (
                      <Typography
                        key={charIndex}
                        variant="caption"
                        sx={{
                          backgroundColor: "#f3e5f5",
                          color: "#673ab7",
                          px: 1,
                          py: 0.5,
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                        }}
                      >
                        {char}
                      </Typography>
                    ))}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAccessory(accessory);
                      }}
                      sx={{
                        borderColor: "#673ab7",
                        color: "#673ab7",
                        "&:hover": {
                          backgroundColor: "#f3e5f5",
                        },
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<WhatsAppIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsApp(`${accessory.name} Bath Accessory inquiry`);
                      }}
                      sx={{
                        backgroundColor: "#25d366",
                        "&:hover": {
                          backgroundColor: "#1eb855",
                        },
                      }}
                    >
                      Inquiry
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Detailed Accessory Modal */}
        <Dialog
          open={!!selectedAccessory}
          onClose={() => setSelectedAccessory(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px" },
          }}
        >
          {selectedAccessory && (
            <>
              <DialogTitle
                sx={{
                  background: getCategoryGradient("Bath Accessories"),
                  color: "white",
                  position: "relative",
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  {selectedAccessory.name}
                </Typography>
                <IconButton
                  onClick={() => setSelectedAccessory(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, p: 3 }}>
                  {/* Left Column */}
                  <Box>
                    <Box
                      sx={{
                        height: 300,
                        background: getCategoryGradient("Bath Accessories"),
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mb: 3,
                      }}
                    >
                      <Box sx={{ textAlign: "center" }}>
                        <InventoryIcon sx={{ fontSize: "4rem", mb: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {selectedAccessory.name}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Key Characteristics
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                      {selectedAccessory.keyCharacteristics.map((char, index) => (
                        <Chip
                          key={index}
                          label={char}
                          size="small"
                          sx={{
                            backgroundColor: "#f3e5f5",
                            color: "#673ab7",
                          }}
                        />
                      ))}
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Materials & Construction
                    </Typography>
                    <List dense>
                      {selectedAccessory.commonMaterials.map((material, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={material}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Right Column */}
                  <Box>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Description
                    </Typography>
                    <Typography variant="body1" mb={3} sx={{ lineHeight: 1.6 }}>
                      {selectedAccessory.description}
                    </Typography>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Primary Applications
                    </Typography>
                    <List dense>
                      {selectedAccessory.primaryUse.map((use, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={use}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Specifications
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" mb={1}>
                        <strong>Material:</strong> {selectedAccessory.specifications.material}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Mounting:</strong> {selectedAccessory.specifications.mounting}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Maintenance:</strong> {selectedAccessory.specifications.maintenance}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Warranty:</strong> {selectedAccessory.warranty}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Use Cases Section */}
                <Box sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                  <Typography variant="h6" fontWeight="bold" mb={3}>
                    Recommended Use Cases
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {selectedAccessory.useCases.map((useCase, index) => (
                      <Paper key={index} sx={{ p: 2, borderRadius: "8px" }}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                          {useCase.location}
                        </Typography>
                        <Typography variant="body2" mb={2} sx={{ lineHeight: 1.5 }}>
                          {useCase.recommendation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Key Checks:</strong> {useCase.keyChecks.join(", ")}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                  onClick={() =>
                    handleShare({
                      title: selectedAccessory.name,
                      description: selectedAccessory.description,
                    })
                  }
                  startIcon={<ShareIcon />}
                  sx={{ color: "#666" }}
                >
                  Share
                </Button>
                {selectedAccessory.images360 && (
                  <Button
                    onClick={() => handle360View(selectedAccessory)}
                    startIcon={<ThreeSixtyIcon />}
                    variant="outlined"
                    sx={{
                      borderColor: "#673ab7",
                      color: "#673ab7",
                    }}
                  >
                    360° View
                  </Button>
                )}
                <Button
                  onClick={() => handleWhatsApp(`${selectedAccessory.name} Bath Accessory inquiry`)}
                  startIcon={<WhatsAppIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "#25d366",
                    "&:hover": {
                      backgroundColor: "#1eb855",
                    },
                  }}
                >
                  WhatsApp Inquiry
                </Button>
                <Button
                  onClick={handleCall}
                  startIcon={<PhoneIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "#673ab7",
                    "&:hover": {
                      backgroundColor: "#512da8",
                    },
                  }}
                >
                  Call Now
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* 360 View Modal */}
        <Dialog
          open={!!view360}
          onClose={() => setView360(null)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              backgroundColor: "#000",
              height: "90vh",
            },
          }}
        >
          {view360 && (
            <>
              <DialogTitle sx={{ color: "white", textAlign: "center", position: "relative", p: 2 }}>
                <Typography variant="h5" component="h2" fontWeight="bold">
                  {view360.name} - 360° Interactive View
                </Typography>
                <IconButton
                  onClick={() => setView360(null)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 0, backgroundColor: "#000", height: "100%" }}>
                <TileViewer360
                  tileName={view360.name}
                  tileImages={view360.images360}
                  onClose={() => setView360(null)}
                />
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default BathAccessoriesPage;
